import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { sanitizeHtmlKeepingStyles } from '../safe-html/styled-html';

interface HtmlSegment {
  kind: 'html';
  html: SafeHtml;
}

interface EmbedSegment {
  kind: 'embed';
  url: SafeResourceUrl;
}

type Segment = HtmlSegment | EmbedSegment;

/**
 * Hosts a video iframe may point at, with the only path shape accepted for each.
 *
 * Anything else is dropped. The path pattern matters as much as the host: it keeps the embed to a video
 * player page and stops a crafted path on an allowed host from being framed.
 */
const ALLOWED_EMBEDS: { hosts: string[]; path: RegExp }[] = [
  { hosts: ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com'], path: /^\/embed\/[A-Za-z0-9_-]{6,20}$/ },
  { hosts: ['player.vimeo.com'], path: /^\/video\/\d{1,15}$/ }
];

/**
 * Renders stored rich text (induction content) safely, including legacy video embeds.
 *
 * The body is rendered with [innerHTML], so Angular's sanitiser applies to everything: scripts, event
 * handlers and javascript: URLs are removed, and so are iframes. Inline styles are kept only for an allow list
 * of formatting properties. The old editor offered
 * a media plugin, so existing induction content can hold <iframe> video embeds that the sanitiser would
 * silently delete. Rather than trusting the whole body to keep them, this component:
 *
 * 1. parses the HTML into an inert document (DOMParser never runs scripts or loads resources);
 * 2. takes out every iframe, and keeps only those whose src is https on an allow listed video host with an
 *    allowed path (see ALLOWED_EMBEDS);
 * 3. rebuilds each kept iframe from the VALIDATED, canonical URL (no query string, no attributes copied
 *    from the original) and trusts only that URL with bypassSecurityTrustResourceUrl();
 * 4. renders the remaining HTML around the embeds through [innerHTML], sanitised by Angular's sanitiser with
 *    allow-listed inline styles put back (sanitizeHtmlKeepingStyles), so colour, alignment and table styling
 *    survive while nothing else does.
 *
 * The author controls nothing about a rendered iframe except which allow listed video it shows.
 */
@Component({
  selector: 'app-safe-rich-content',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <ng-container *ngFor="let segment of segments">
      <div *ngIf="segment.kind === 'html'" [innerHTML]="segment.html"></div>
      <div *ngIf="segment.kind === 'embed'" class="embed-responsive embed-responsive-16by9 my-2">
        <iframe
          class="embed-responsive-item"
          title="Embedded video"
          loading="lazy"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          allow="encrypted-media; picture-in-picture; fullscreen"
          [src]="segment.url"></iframe>
      </div>
    </ng-container>
  `
})
export class SafeRichContentComponent implements OnChanges {
  @Input() html: string | null | undefined = '';

  segments: Segment[] = [];

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(): void {
    this.segments = this.buildSegments(this.html);
  }

  private buildSegments(html: string | null | undefined): Segment[] {
    if (!html) {
      return [];
    }

    // Inert parse: no scripts run and no resources load in a DOMParser document.
    const doc: Document = new DOMParser().parseFromString(String(html), 'text/html');
    const embedUrls: string[] = [];

    // A random marker, so content cannot collide with it by accident. It carries no security weight: a marker
    // a user typed could at worst point at an already validated embed.
    const nonce: string = Math.random().toString(36).slice(2);

    Array.from(doc.body.querySelectorAll('iframe')).forEach((iframe: HTMLIFrameElement): void => {
      const url: string | null = this.canonicalEmbedUrl(iframe.getAttribute('src'));
      if (url) {
        embedUrls.push(url);
        iframe.replaceWith(doc.createTextNode(`@@SC_EMBED_${nonce}_${embedUrls.length - 1}@@`));
      } else {
        iframe.remove();
      }
    });

    const marker: RegExp = new RegExp(`@@SC_EMBED_${nonce}_(\\d+)@@`);
    const segments: Segment[] = [];
    // split() with a capture group returns [html, index, html, index, ...].
    doc.body.innerHTML.split(marker).forEach((part: string, position: number): void => {
      if (position % 2 === 1) {
        const url: string | undefined = embedUrls[Number(part)];
        if (url) {
          // Trusted only because the URL was rebuilt from an allow listed host and path above.
          segments.push({ kind: 'embed', url: this.sanitizer.bypassSecurityTrustResourceUrl(url) });
        }
      } else if (part.trim()) {
        segments.push({ kind: 'html', html: sanitizeHtmlKeepingStyles(this.sanitizer, part) });
      }
    });

    return segments;
  }

  /**
   * Returns a clean https URL for an allow listed video, or null when the src is not one.
   * The query string and fragment are discarded on purpose, so only the video id survives.
   */
  private canonicalEmbedUrl(src: string | null): string | null {
    if (!src) {
      return null;
    }

    let parsed: URL;
    try {
      // A protocol relative src (//www.youtube.com/...) is common in old embeds; resolve it as https.
      parsed = new URL(src.trim().startsWith('//') ? 'https:' + src.trim() : src.trim());
    } catch {
      return null;
    }

    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port) {
      return null;
    }

    const allowed = ALLOWED_EMBEDS.find((rule) => rule.hosts.includes(parsed.hostname.toLowerCase()));
    if (!allowed || !allowed.path.test(parsed.pathname)) {
      return null;
    }

    return 'https://' + parsed.hostname.toLowerCase() + parsed.pathname;
  }
}
